import React, { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, CircularProgress, Grid, IconButton, Tooltip } from '@mui/material'
import TuneIcon from '@mui/icons-material/Tune'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import { pdf } from '@react-pdf/renderer'
import moment from 'moment'
import API from '../../../services/API'
import { GlobalContext, TenantSettingsContext, UserContext } from '../../../App'
import { LoadingSmall } from '../../loading/loadingPage'
import KPI_SECTIONS from './sections'
import useKpiDateRange from './hooks/useKpiDateRange'
import useKpi from './hooks/useKpi'
import KPIPageLayout from './components/KPIPageLayout'
import SectionScoreCard from './components/SectionScoreCard'
import AttentionList from './components/AttentionList'
import KPIReportPDF from './KPIReportPDF'

const KPIHome = () => {
  const navigate = useNavigate()
  const dateRange = useKpiDateRange()
  const { data, loading, error } = useKpi('/kpi-dashboard/overview', dateRange.query)
  const { user } = useContext(UserContext)
  const { setToast } = useContext(GlobalContext)
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig
  const [generatingReport, setGeneratingReport] = useState(false)

  const summaries = Object.fromEntries(
    (data?.sections ?? []).map((section) => [section.slug, section]),
  )
  const attention = (data?.sections ?? []).flatMap((section) => section.attention ?? [])

  const handleDownloadReport = async () => {
    setGeneratingReport(true)
    try {
      const responses = await Promise.all(
        KPI_SECTIONS.map((section) =>
          API.get(`/kpi-dashboard/section/${section.slug}?${dateRange.query}`),
        ),
      )
      if (responses.some((res) => res.status !== 200)) {
        setToast({ message: 'Unable to gather KPI data for the report', severity: 'error' })
        return
      }
      const startLabel = moment(dateRange.value.startDate).format('Do MMMM YYYY')
      const endLabel = moment(dateRange.value.endDate).format('Do MMMM YYYY')
      const blob = await pdf(
        <KPIReportPDF
          sections={responses.map((res) => res.data)}
          orgSettings={orgSettings}
          rangeLabel={`${startLabel} – ${endLabel}`}
          printedBy={`Generated by ${user.firstName} ${user.lastName} on ${moment().format('Do MMMM YYYY, HH:mm')}`}
        />,
      ).toBlob()

      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `KPI_Report_${moment(dateRange.value.startDate).format('DDMMYYYY')}-${moment(dateRange.value.endDate).format('DDMMYYYY')}.pdf`
      link.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      console.error(e)
      setToast({ message: 'Unable to generate the PDF report', severity: 'error' })
    } finally {
      setGeneratingReport(false)
    }
  }

  return (
    <KPIPageLayout
      title="KPIs & Analytics"
      subtitle="Live performance across every part of the service"
      dateRange={dateRange}
      actions={
        <>
          <Tooltip title="Download PDF report for the selected date range">
            <span>
              <IconButton onClick={handleDownloadReport} disabled={generatingReport || loading}>
                {generatingReport ? <CircularProgress size={22} /> : <PictureAsPdfIcon />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="KPI target settings">
            <IconButton onClick={() => navigate(`/kpi/settings${dateRange.search}`)}>
              <TuneIcon />
            </IconButton>
          </Tooltip>
        </>
      }
    >
      {error && <Alert severity="error">Unable to load the KPI overview. Please try again.</Alert>}
      {loading ? (
        <LoadingSmall />
      ) : (
        <>
          <AttentionList items={attention} search={dateRange.search} />
          <Grid container spacing={2}>
            {KPI_SECTIONS.map((section) => (
              <Grid item xs={12} sm={6} lg={4} key={section.slug}>
                <SectionScoreCard
                  section={section}
                  summary={summaries[section.slug]}
                  search={dateRange.search}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </KPIPageLayout>
  )
}

export default KPIHome
